/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

import pluginFactory from 'taoTests/runner/plugin';
import fullscreenApiFactory from './fullscreenApi.js';
import { isPausedByProctorUiFlow } from '../../../util/proctoring';
import { debounce, defaults } from 'lodash';
import { showNavigationFeedback, getNavigationFeedbacksStore } from '../../../feedback';
import { getNavigationFeedbackConfig } from 'testRunnerDynamicModulesIndex';
import { securityLog } from '../common/securityLog.js';
import { addHideOnPrintStyle } from '../../tools/print/util.js';
import fullScreenKeyboardInputObserver from '../common/fullscreenInputObserver.js';
import { isNavigationDisabledByTimers } from '../../../timers/navigation.js';

const defaultConfig = {
    autoresume: true,
    exitOnFinish: true,
    listenerTimeout: 200
};
/**
 * This plugin forces the test runner to be only used in full screen mode.
 */
export default pluginFactory({
    name: 'forceFullscreen',

    install() {
        const testRunner = this.getTestRunner();
        const providedConfig = testRunner.getPluginConfig(this.getName()) || {};
        const pluginConfig = defaults({}, providedConfig, defaultConfig);
        const testConfig = testRunner.getConfig();
        const serviceCallId = testConfig.serviceCallId;

        this.navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);

        /**
         * Helper to work with browser fullscreen mode.
         */
        this.browserApi = fullscreenApiFactory();

        /**
         * Reset feedbacks store
         * Necessary because its state can be wrong if user manipulated DOM
         */
        this.setModalClosed = () => {
            this.navigationFeedbacksStore.cancel(feedback => feedback?.feedbackTypeArgs?.isSecurity);
        };

        /**
         * Listen browser API to detect when fullscreen is off
         * Listen testRunner resume event
         */
        this.eventListener = debounce(() => {
            if (!this.isFileSelection && this.fullscreenInputObserver?.isFullScreenAllowed()) {
                this.checkAndForceFullscreen(false);
            }
        }, pluginConfig.listenerTimeout);

        /**
         * Return all file inputs in the test
         * @returns {NodeListOf<Element>} - all file inputs in the test
         */
        this.getFileInputs = () => document.querySelectorAll("input[type='file']");

        /**
         * Handler of file selection exit
         * @returns {void}
         */
        this.exitFromFileSelection = () => {
            this.isFileSelection = false;
            window.removeEventListener('focus', this.exitFromFileSelection);
            // try to enter fullscreen again if it was exited
            if (!this.browserApi.isFullscreen()) {
                this.browserApi.enterFullscreen();
            }
            this.checkAndForceFullscreen(true);
        };

        /**
         * Handler of file selection
         * @returns {void}
         */
        this.handleFileUploadClick = () => {
            this.isFileSelection = true;
            window.addEventListener('focus', this.exitFromFileSelection);
        };
        /**
         * When browser print dialog is opened, fullscreen is exited.
         *  - if by Ctrl+P, fullscreen exit is detected before print. (simultaneously? In Safari, security-modal seems to take space and cut item content)
         *  - if by clicking printPlugin's button, fullscreen exit is not detected.
         * Ensure fullscreen after print dialog is closed.
         */
        this.handleAfterPrint = () => {
            //If 'print' plugin toolbar button click (not Ctrl+P, behavior is different for Ctrl+P, use `disableCommands` plugin):
            //  - Firefox: is still in fullscreen
            //  - Chrome/Edge: not in fullscreen, re-enters programmatically
            //      - note: fullscreen is actually exited in `beforeprint`,
            //        so while Print dialog is open, user can switch to other program window and nothing will be detected or logged.
            //  - Safari: not in fullscreen, will not re-enter programmatically, will not log exit-fullscreen-attempt
            //       - note: will log blur-attempt on `beforeprint` if `pauseOnBlur` plugin (but that's the same for FileUpload...)
            const isFullscreen = this.browserApi.isFullscreen();
            if (!isFullscreen) {
                const enterFullscreenPromise = this.browserApi.enterFullscreen() || Promise.resolve();
                enterFullscreenPromise
                    .catch(() => {})
                    .finally(() => {
                        this.checkAndForceFullscreen(false);
                    });
            }
        };

        /**
         * Check if fullscreen is off and force fulscreen after message is shown
         * @param {Boolean} doNotRaiseSecurityEvents
         */
        this.checkAndForceFullscreen = (doNotRaiseSecurityEvents = false) => {
            const isFullscreen = this.browserApi.isFullscreen();
            const isSecurityMessage = this.navigationFeedbacksStore.isSecurityShown();

            if (isFullscreen && isSecurityMessage) {
                this.navigationFeedbacksStore.cancel();
            } else if (!isFullscreen && !isPausedByProctorUiFlow(testRunner) && !isSecurityMessage) {
                this.navigationFeedbacksStore.cancel();
                this.showMessage(doNotRaiseSecurityEvents);
                if (!doNotRaiseSecurityEvents) {
                    securityLog(testRunner, 'exit-fullscreen-attempt');
                }
            }
        };

        /**
         * Show message
         * @param {Boolean} doNotRaiseSecurityEvents
         */
        this.showMessage = doNotRaiseSecurityEvents => {
            testRunner.trigger('disablenav');
            const feedbackTypeArgs = { isSecurity: true };
            const feedbackConfig = getNavigationFeedbackConfig(feedbackTypeArgs, {
                testRunnerPlugins: testRunner.getPlugins()
            });
            showNavigationFeedback(feedbackTypeArgs, feedbackConfig)
                .then(feedbackResult => {
                    if (!doNotRaiseSecurityEvents) {
                        testRunner.trigger('security-closed', {
                            plugin: this.getName(),
                            autoresume: pluginConfig.autoresume
                        });
                    }
                    const disableNav = isNavigationDisabledByTimers(
                        serviceCallId,
                        testRunner.getDataHolder()?.getCurrentTestPart(),
                        testRunner.getDataHolder()?.getCurrentItem()?.id
                    );
                    if (!disableNav) {
                        testRunner.trigger('enablenav');
                    }
                    if (!feedbackResult.cancelled) {
                        //if cancelled by going to fullscreen by F11
                        // or proctor-pause or proctor-terminate, do not do anything
                        this.browserApi.enterFullscreen();
                    }
                })
                .catch(err => {
                    testRunner.trigger('error', err);
                });
            if (!doNotRaiseSecurityEvents) {
                testRunner.trigger('security-showed', {
                    plugin: this.getName(),
                    autoresume: pluginConfig.autoresume,
                    action: 'pause',
                    category: 'examinee',
                    subcategory: 'navigation',
                    message: 'Test taker tried to go out from full screen mode.'
                });
            }
        };

        if (pluginConfig.exitOnFinish) {
            let canExitFullscreen = true;
            testRunner
                .on('testfinished.leaveFullscreen', context => {
                    if (context && context.nextDeliveryExecutionUrl) {
                        // in the middle of a test battery, we must keep the fullscreen
                        canExitFullscreen = false;
                    }
                })
                .on('finish.leaveFullscreen', () => {
                    testRunner.off('.leaveFullscreen');
                    if (canExitFullscreen) {
                        this.browserApi.exitFullscreen();
                    }
                });
        }
    },
    /**
     * Initialize plugin (called during runner's initialization)
     */
    init() {
        this.enable();
    },

    /**
     * Destroy plugin (called during runner's destruction)
     */
    destroy() {
        this.disable();
    },

    /**
     * Enable plugin
     */
    enable() {
        this.checkAndForceFullscreen(true);
        //as because enable() is called on plugin init, before item being rendered, navigation doesn't get disabled
        //and if fullscreen modal gets removed or hidden, the navigation is enabled under it
        //for this specific case disable navigation once the item has been rendered
        this.getTestRunner().on(`renderitem.${this.getName()}.initial`, () => {
            //we have to disable the navigation only under the security modal
            //otherwise it can break the navigation in batteries
            if (this.navigationFeedbacksStore.isSecurityShown()) {
                this.getTestRunner().trigger('disablenav');
            }
            this.getTestRunner().off(`renderitem.${this.getName()}.initial`);
        });

        this.browserApi.addChangeListener(this.eventListener);

        window.addEventListener('afterprint', this.handleAfterPrint);
        this.printStyleTag?.remove();
        this.printStyleTag = addHideOnPrintStyle();

        this.getTestRunner()
            .on(`move.${this.getName()}`, this.setModalClosed)
            .on(`enableitem.${this.getName()}`, this.eventListener)
            .on(`renderitem.${this.getName()}`, () => {
                this.eventListener();
                this.getFileInputs().forEach(fileInput => {
                    fileInput.addEventListener('click', this.handleFileUploadClick);
                });
                this.fullscreenInputObserver = fullScreenKeyboardInputObserver();
                this.fullscreenInputObserver.observeFullScreenKeyboardInput(this.browserApi);
            });
    },
    /**
     * Disable plugin
     */
    disable() {
        this.browserApi.removeChangeListener(this.eventListener);
        window.removeEventListener('afterprint', this.handleAfterPrint);
        this.printStyleTag?.remove();
        this.getTestRunner().off(`.${this.getName()}`);
        this.fullscreenInputObserver?.unsubscribe();
    }
});
